/**
 * bulkSelect.ts — Pure helper for the TableView bulk multi-select feature.
 *
 * All functions are side-effect-free (no signals, no DOM). The TableView owns
 * the actual Set<number> state; this module only provides the operations and
 * derived calculations so they can be unit-tested in isolation.
 */

import type { TaskPriority } from "@juuplan/shared";

// ---------- selection operations ----------

/**
 * Toggle a single id in the selection set.
 * Returns a new Set — does NOT mutate the input.
 */
export function toggleId(selected: Set<number>, id: number): Set<number> {
  const next = new Set(selected);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}

/**
 * Select all ids that are currently visible/eligible.
 * If every eligible id is already selected, clears the selection instead
 * (toggle-all behaviour, same as Monday/Linear).
 *
 * Returns a new Set.
 */
export function toggleAll(
  selected: Set<number>,
  eligibleIds: number[]
): Set<number> {
  const allSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selected.has(id));
  if (allSelected) {
    return new Set<number>();
  }
  return new Set<number>(eligibleIds);
}

/**
 * Toggle a *section's* ids as a group, preserving selections outside the
 * section. Used by the per-phase / per-bucket select-all checkbox so that
 * checking one phase's header selects only that phase's tasks and leaves other
 * phases' selections untouched (unlike `toggleAll`, which replaces the whole
 * selection).
 *
 * - If every id in the section is already selected → remove them all.
 * - Otherwise → add all section ids to the current selection.
 *
 * Returns a new Set — does NOT mutate the input.
 */
export function toggleSection(
  selected: Set<number>,
  sectionIds: number[]
): Set<number> {
  const next = new Set(selected);
  const allSelected =
    sectionIds.length > 0 && sectionIds.every((id) => next.has(id));
  if (allSelected) {
    for (const id of sectionIds) next.delete(id);
  } else {
    for (const id of sectionIds) next.add(id);
  }
  return next;
}

/**
 * Derive the "select-all" checkbox indeterminate state:
 * - false  → none selected or all selected (determinate)
 * - true   → some but not all selected
 */
export function isIndeterminate(
  selected: Set<number>,
  eligibleIds: number[]
): boolean {
  if (eligibleIds.length === 0) return false;
  const count = eligibleIds.filter((id) => selected.has(id)).length;
  return count > 0 && count < eligibleIds.length;
}

/**
 * Whether all eligible ids are selected (used for the checked state of
 * the select-all checkbox).
 */
export function isAllSelected(
  selected: Set<number>,
  eligibleIds: number[]
): boolean {
  return eligibleIds.length > 0 && eligibleIds.every((id) => selected.has(id));
}

// ---------- patch builders (kept typed here, used in the UI) ----------

export interface BulkStatusPatch {
  kind: "status";
  status_id: number;
}

export interface BulkPriorityPatch {
  kind: "priority";
  priority: TaskPriority | null;
}

export type BulkPatch = BulkStatusPatch | BulkPriorityPatch;

/**
 * Convert a BulkPatch into the `patch` body accepted by PATCH /api/tasks/batch.
 */
export function patchBody(
  bulk: BulkPatch
): { status_id: number } | { priority: TaskPriority | null } {
  if (bulk.kind === "status") {
    return { status_id: bulk.status_id };
  }
  return { priority: bulk.priority };
}
